/**
 * The header tabs' screens in other exports, or across the EPUB boundary: both exports' toc.json
 * fetched and matched by toc names (comparator/src/tocMatch.js - the rule the comparator pairs
 * screens by), for `resolveAppLinks`'s `matchedScreenIds`. Plain JavaScript like
 * appHeaderLinks.js, so epub-reader can import it from outside its own sources.
 */
import {
  counterpartScreenId,
  listTocScreens,
  matchScreenLists,
} from "../../../../comparator/src/tocMatch.js";
import { courseTocUrl, screenMatchTargets } from "./appHeaderLinks";

/** @type {Record<string, Promise<import("../../../../comparator/src/tocMatch.js").TocScreen[] | null>>} */
var screenLoads = {};
/** @type {Record<string, import("../../../../comparator/src/tocMatch.js").ScreenMatch>} */
var matches = {};

/**
 * The screens of the toc at `url`, one GET per URL while it is pending or once it succeeded;
 * null when it is missing or unreadable (asked again next time).
 * @param {string} url
 * @param {boolean} epub
 */
function loadScreens(url, epub) {
  if (!screenLoads[url]) {
    screenLoads[url] = (typeof fetch === "function" ? fetch(url) : Promise.reject())
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (toc) {
        return toc ? listTocScreens(toc, { epub: epub }) : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (screens) {
        if (!screens) {
          delete screenLoads[url];
        }
        return screens;
      });
  }
  return screenLoads[url];
}

/**
 * The screen of `screenId` in each export of screenMatchTargets, keyed `<exportId>_<renderer>`;
 * a target is left out when either toc cannot be read.
 * @param {string} href  the page URL
 * @param {string} app  the page's app ("bv" | "express" | "epub")
 * @param {string | undefined} exportId
 * @param {string | undefined} screenId
 * @returns {Promise<Record<string, string>>}
 */
export function loadMatchedScreenIds(href, app, exportId, screenId) {
  var ownUrl = courseTocUrl(href, exportId, app);
  var targets = screenId && ownUrl ? screenMatchTargets(href, app, exportId) : [];
  return Promise.all(
    targets.map(function (target) {
      var url = courseTocUrl(href, target.exportId, target.renderer);
      if (!url || !ownUrl) {
        return null;
      }
      return Promise.all([
        loadScreens(ownUrl, app === "epub"),
        loadScreens(url, target.renderer === "epub"),
      ]).then(function (lists) {
        var own = lists[0];
        var other = lists[1];
        if (!own || !other) {
          return null;
        }
        var key = ownUrl + " " + url;
        matches[key] = matches[key] || matchScreenLists(own, other);
        var matched = counterpartScreenId(own, other, matches[key], screenId || "", 1);
        return matched ? [target.exportId + "_" + target.renderer, matched] : null;
      });
    }),
  ).then(function (entries) {
    /** @type {Record<string, string>} */
    var result = {};
    entries.forEach(function (entry) {
      if (entry) {
        result[entry[0]] = entry[1];
      }
    });
    return result;
  });
}
